<template>
  <div class="section-container" id="development-process">
    <div class="section-container-header-box">
      <p class="list-text">Our process</p>
    </div>
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        v-html="line.text"
        :class="{ 'special-text': line.special }"></p>
    </h2>

    <div class="development-process-steps">
      <div
        class="development-process-steps-box"
        v-for="(step, index) in developmentProcessSteps"
        :key="index"
        :class="{
          'development-process-steps-box-visible': visibleSteps.has(index),
        }"
        :ref="(el) => el && (stepRefs[index] = el as HTMLElement)">
        <div class="development-process-steps-box-step">
          <div class="development-process-steps-box-step-title">
            <p class="medium-text-light disabled-text">{{ step.step }}</p>
            <p class="heading-2">{{ step.title }}</p>
          </div>

          <p class="body-text">
            {{ step.description }}
          </p>
          <a
            :href="step.link"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="step.action"
            class="arrow-button"
            v-if="step.action && step.link">
            <p class="list-text-bold">{{ step.action }}</p>
            <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
          </a>
        </div>
        <div
          class="development-process-steps-box-tracker"
          :class="{ 'development-process-steps-box-tracker-active': activeStep === index }">
          <div class="development-process-steps-box-tracker-dot"></div>
        </div>
        <div class="development-process-steps-box-step"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';
useFadeUpObserver();

const lines = [
  { text: `How the magic <span class="magic-i-effect">i</span>s `, special: false },
  { text: 'made.', special: true },
];

const developmentProcessSteps = [
  {
    step: 'Step 1',
    title: 'Discovery Call',
    description:
      'We start by learning about your vision and goals in a discovery call to ensure alignment on the project direction.',
    action: 'Book a free call',
    link: 'https://calendly.com/vortec-io',
  },
  {
    step: 'Step 2',
    title: 'Planning & Strategy',
    description:
      'Together, we create a clear roadmap and strategy tailored to your needs, setting the foundation for successful execution.',
  },
  {
    step: 'Step 3',
    title: 'Design & Development',
    description:
      'Our team designs intuitive experiences and builds reliable solutions, turning ideas into high-performing digital products.',
  },
  {
    step: 'Step 4',
    title: 'Launch & Support',
    description:
      'We manage a seamless launch and provide continuous support to ensure your product grows and performs at its best.',
  },
];

const activeStep = ref<number | null>(null);
const stepRefs = ref<HTMLElement[]>([]);
const visibleSteps = ref<Set<number>>(new Set<number>());
let observer: IntersectionObserver | null = null;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const idx = stepRefs.value.indexOf(entry.target as HTMLElement);
        if (idx === -1) {
          return;
        }

        if (entry.isIntersecting) {
          visibleSteps.value.add(idx);
        } else {
          const lastidx = Math.max(...Array.from(visibleSteps.value));
          if (lastidx === idx) {
            visibleSteps.value.delete(idx);
          }
        }
      });

      if (visibleSteps.value.size > 0) {
        activeStep.value = Math.max(...Array.from(visibleSteps.value));
      } else {
        activeStep.value = null;
      }
    },
    {
      threshold: 0.5,
      rootMargin: '0px 0px -100px 0px',
    }
  );
  stepRefs.value.forEach((el) => observer?.observe(el));
});

onBeforeUnmount(() => {
  stepRefs.value.forEach((el) => observer?.unobserve(el));
});
</script>
